import React from 'react'
import queryString from 'query-string'
import { useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { ExternalLink } from '@/components/ExternalLink'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

/**
 * @param {string[]} [props.scorecardSids] Pins the link's scorecard scope. Drawers
 *   narrow to their own scorecard, which the global filter bar doesn't know about.
 */
export const ExternalCallIdsLink = ({
  callIds,
  passedOrgId,
  channelType,
  scorecardSids: pinnedScorecardSids,
  ...props
}) => {
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const { filters } = useSelector((state) => state.qaCopilot)
  // If Balto admin, use dashboard filter. If filter is empty, use current user's org id
  const organizationId = passedOrgId || filters.organizationId || currentUserOrgId
  const limitedCallIds = Array.isArray(callIds) ? callIds.slice(0, 400) : callIds
  const requestedScorecardSids = isNil(pinnedScorecardSids)
    ? filters.scorecards?.map((scorecard) => scorecard.value)
    : pinnedScorecardSids
  // ff968c024: appending scorecard uuids to a "Not Scored" link broke the
  // Call Explorer redirect. The guard sits on the resolved value rather
  // than the filter-bar branch so a pinned override can't route around
  // it -- the drawers forward scorecards while dropping scoredStatus, so
  // "Not Scored" plus a pinned sid is reachable.
  const scorecardSids = filters.scoredStatus === 'unscored' ? undefined : requestedScorecardSids
  const queryParams = queryString.stringify({
    call_ids: limitedCallIds,
    organizationId,
    channel: channelType,
    scorecards: scorecardSids,
  })

  if (isEmpty(callIds)) {
    // Keep the caller's className so an unlinked label sits in its row
    // looking the same as the linked ones around it.
    return <span className={props.className}>{props.label}</span>
  }

  const showTooltip = Array.isArray(callIds) && callIds.length > 400
  const isSingleCall = !Array.isArray(callIds) || callIds.length === 1
  const baseUrl = isSingleCall ? '/calls' : '/explorer'

  return (
    <InteractiveTooltip
      hide={!showTooltip}
      content="Only the latest 400 calls are shown in Call Explorer."
    >
      <ExternalLink url={`${baseUrl}?${queryParams}`} target="_blank" {...props} />
    </InteractiveTooltip>
  )
}
